/**
 * BoletoPaymentPanel — Painel premium de pagamento via Boleto.
 *
 * Layout: linha digitável + botões "Visualizar boleto" / "Baixar boleto" +
 * iframe responsivo (60vh, mín 600px) com o boleto completo do provider.
 *
 * RELEASE: 2026-06-21 v2.2.0 — Botões "Visualizar boleto" e "Baixar boleto"
 *                              com estilo sólido idêntico (lado a lado, mesma
 *                              largura). Ambos abrem o PDF direto
 *                              (`bank_slip_url`) em vez da página hospedada
 *                              do Asaas (`invoice_url`), que apenas mostra o
 *                              preview da cobrança.
 * RELEASE: 2026-06-21 v2.1.0 — Iframe usa `bank_slip_url` (PDF do boleto,
 *                              embeddável). `invoice_url` (página hospedada do
 *                              Asaas) é bloqueada por X-Frame-Options. Botões
 *                              "Visualizar/Baixar" usam o melhor URL disponível
 *                              e os blocos renderizam quando qualquer um dos
 *                              dois URLs existe.
 * RELEASE: 2026-06-21 v2.0.2 — Iframe do boleto com fundo branco e altura
 *                              reduzida em 15% (remove bordas pretas).
 * RELEASE: 2026-06-21 v2.0.1 — Iframe do boleto usa hint de PDF
 *                              (toolbar=0, navpanes=0, view=FitH) para
 *                              esconder a chrome do visualizador nativo.
 * RELEASE: 2026-06-21 v2.0.0 — Redesign premium com iframe scrollável do
 *                              boleto, botões pretos estilo "fatura" e aviso
 *                              de compensação destacado.
 * RELEASE: 2026-06-20 v1.1.0 — Envelope padronizado de erro.
 * RELEASE: 2026-06-20 v1.0.0 — Sub-fase B (Boleto).
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, CheckCircle2, RefreshCw, ExternalLink, Download, Clock } from 'lucide-react';
import { CheckoutErrorBanner } from './components/CheckoutErrorBanner';
import { getFallbackMessage, getNetworkErrorMessage } from '@/lib/checkout/userMessages';

const logger = createLogger('BoletoPaymentPanel');

export interface BoletoPaymentPanelProps {
  slug: string;
  /** E-mail do pagador (usado no aviso de compensação). */
  email?: string | null;
}

interface BoletoData {
  linha_digitavel: string | null;
  nosso_numero: string | null;
  bar_code: string | null;
  /** Página hospedada do recibo (Asaas /i/{id}). Bloqueada em iframe (X-Frame-Options). Usada nos botões. */
  invoice_url: string | null;
  /** PDF direto do boleto (Asaas /b/pdf/{id}). Embeddável em iframe via PDF viewer nativo. */
  bank_slip_url: string | null;
}

/** Formata linha digitável agrupando 5/5/5/5/5/6/1 (padrão Febraban). */
function formatLinhaDigitavel(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length < 47) return raw;
  return `${d.slice(0, 5)}.${d.slice(5, 10)} ${d.slice(10, 15)}.${d.slice(15, 21)} ${d.slice(21, 26)}.${d.slice(26, 32)} ${d.slice(32, 33)} ${d.slice(33)}`;
}

export function BoletoPaymentPanel({ slug, email }: BoletoPaymentPanelProps) {
  const [boleto, setBoleto] = useState<BoletoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<
    { message: string; code?: string | null; supportId?: string | null } | null
  >(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setErrorState(null);
    setLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke(
        'public-checkout',
        { body: { action: 'get_boleto', slug } },
      );
      if (invokeErr) {
        logger.warn('Falha de rede no get_boleto', { err: invokeErr.message });
        setErrorState({ message: getNetworkErrorMessage(), code: 'NETWORK_ERROR' });
        return;
      }
      const payload = (data ?? {}) as BoletoData & {
        success?: boolean;
        error_code?: string;
        user_message?: string;
        support_id?: string;
      };
      if (payload?.success === false || payload?.error_code) {
        setErrorState({
          message: payload.user_message || getFallbackMessage(payload.error_code),
          code: payload.error_code ?? null,
          supportId: payload.support_id ?? null,
        });
        return;
      }
      setBoleto(payload);
    } catch (err) {
      logger.error('Falha ao carregar boleto', { err: String(err) });
      setErrorState({ message: getNetworkErrorMessage(), code: 'NETWORK_ERROR' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleCopy = async () => {
    const text = boleto?.linha_digitavel?.replace(/\D/g, '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      logger.warn('Clipboard write failed', { err: String(err) });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-2 md:p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[60vh] min-h-[600px] w-full rounded-xl" />
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="space-y-3 p-4">
        <CheckoutErrorBanner
          message={errorState.message}
          code={errorState.code ?? undefined}
          supportId={errorState.supportId ?? undefined}
        />
        <Button size="sm" variant="outline" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Tentar novamente
        </Button>
      </div>
    );
  }

  const formatted = boleto?.linha_digitavel
    ? formatLinhaDigitavel(boleto.linha_digitavel)
    : '';

  return (
    <div className="space-y-6">
      {/* Cabeçalho do método — alinhado com o PIX/Cartão */}
      <header className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Pague em qualquer banco
          </p>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BoletoGlyph className="text-slate-700" /> Boleto bancário
          </h3>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-slate-50 border-slate-200 text-slate-700">
          <Clock className="w-3 h-3" />
          Compensa em até 3 dias úteis
        </span>
      </header>

      {/* Linha digitável + ações */}
      {boleto?.linha_digitavel && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Linha digitável
          </p>
          <div className="flex flex-col md:flex-row gap-2.5 items-stretch md:items-center">
            <button
              type="button"
              onClick={handleCopy}
              className="group flex-1 min-w-0 flex items-center justify-between gap-3 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white transition text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
            >
              <span className="text-sm font-mono text-slate-800 truncate">
                {formatted}
              </span>
              <span
                className={
                  'flex items-center gap-1.5 text-xs font-semibold flex-shrink-0 transition-colors ' +
                  (copied ? 'text-emerald-600' : 'text-slate-700 group-hover:text-slate-900')
                }
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" /> Copiar
                  </>
                )}
              </span>
            </button>
            {(boleto?.bank_slip_url || boleto?.invoice_url) && (
              <div className="flex gap-2 flex-none">
                <Button
                  asChild
                  size="sm"
                  className="flex-none bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <a
                    href={(boleto.bank_slip_url || boleto.invoice_url) as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Visualizar boleto"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Visualizar
                  </a>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="flex-none bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <a
                    href={(boleto.bank_slip_url || boleto.invoice_url) as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    title="Baixar boleto"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Baixar
                  </a>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Iframe do boleto completo (PDF). */}
      {(boleto?.bank_slip_url || boleto?.invoice_url) && (() => {
        const baseUrl = (boleto.bank_slip_url || boleto.invoice_url) as string;
        const viewerHint = '#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width';
        const pdfUrl = `${baseUrl}${viewerHint}`;
        return (
          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <iframe
              src={pdfUrl}
              title="Boleto completo"
              className="w-full h-[51vh] min-h-[510px] max-h-[765px] bg-white"
            />
          </div>
        );
      })()}

      {/* Aviso de compensação */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200/70">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5 text-sm">
          <p className="font-semibold text-slate-900">Já pagou este boleto?</p>
          <p className="text-slate-700">
            A confirmação pode levar até <strong>3 dias úteis</strong>.{' '}
            {email ? (
              <>
                Você receberá o comprovante no e-mail{' '}
                <strong className="text-slate-900">{email}</strong>.
              </>
            ) : (
              <>O comprovante será enviado ao e-mail cadastrado.</>
            )}
          </p>
        </div>
      </div>

      {boleto?.nosso_numero && (
        <p className="text-[11px] text-slate-400 font-mono text-right">
          Nosso número · {boleto.nosso_numero}
        </p>
      )}
    </div>
  );
}

/** Glifo simples de código de barras para o header. */
function BoletoGlyph({ className = '' }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <rect x="3" y="4" width="1.5" height="16" fill="currentColor" />
      <rect x="6" y="4" width="2.5" height="16" fill="currentColor" />
      <rect x="10" y="4" width="1.5" height="16" fill="currentColor" />
      <rect x="13" y="4" width="2.5" height="16" fill="currentColor" />
      <rect x="17.5" y="4" width="1" height="16" fill="currentColor" />
      <rect x="20" y="4" width="1.5" height="16" fill="currentColor" />
    </svg>
  );
}
